import { createAction } from '@reduxjs/toolkit'
import isEmpty from 'lodash/isEmpty'
import { all, call, ForkEffect, put, select, takeEvery } from 'redux-saga/effects'

import { getFlagTypeById } from '../utils/getFlagTypeById'
import { createFlag } from '../../../api/projects-api'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { FlagAPIPayload, FlagFilters, FlagFormData, NewOpeningLink, OpeningLink } from '../../../models/flags'
import { Project } from '../../../models/project'
import { AzureFeatureFlagIds } from '../../../shared/constants/azure-feature-flags'
import { DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { isAzureFeatureFlagEnabled } from '../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import { filterNullValuesFromObject } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import addTooltipsToFlag from '../../lib/utils/functionality-bindings/addTooltipsToFlag'
import { selectActiveHighlightOpeningId, selectDrawableLocations } from '../../slices/2D'
import { selectActiveDocumentChunkIds } from '../../slices/documents'
import { selectAzureFeatureFlags } from '../../slices/flags'
import { ActiveGeometryGroup, GeometricDrawable, selectActiveGeometryGroup } from '../../slices/geometry'
import { addNewFlag, selectFlagFilters, selectMaterialFlagIndexByOpeningId } from '../../slices/materialFlags'
import { selectProject } from '../../slices/project'
import { IMUP2DDrawableLocation, Vector2D } from '../../types'

export const createFlagAction = createAction<FlagFormData>('createFlag')

function* generateOpeningLinksForDrawables(
    drawables: GeometricDrawable[],
    workspaceTool: Workspace,
    flagTool: Flag,
    materialFlagIndexByOpeningId: ReturnType<typeof selectMaterialFlagIndexByOpeningId>
) {
    const openingLinks: NewOpeningLink[] = []

    for (const drawable of drawables) {
        const drawableAreaItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) => data.drawable_id === drawable.id && data.shapeType === DRAWING_TYPES.AREA
        )

        const nextIndex = isEmpty(materialFlagIndexByOpeningId?.[drawable.id])
            ? 0
            : Object.keys(materialFlagIndexByOpeningId[drawable.id]).length

        const drawableCenterCoordinates: Vector2D = yield call(
            flagTool.getFlagPointFromCoordinates,
            drawable.opening_locations[0].coordinates,
            !!drawableAreaItems.length,
            nextIndex
        )

        openingLinks.push({
            opening_id: drawable.id,
            document_chunk_id: drawable.opening_locations[0].document_chunk_id,
            coordinates: drawableCenterCoordinates,
        })
    }

    return openingLinks
}

/**
 * Highlights aren't GeometricDrawables (they're deliberately excluded from the geometry/material
 * catalog -- see geometry.ts's handleGotOpeningGroups), so they can't go through
 * generateOpeningLinksForDrawables. They do carry the same coordinates/document_chunk_id shape
 * via the generic drawableLocations list every opening (material or highlight) hydrates from.
 */
export function* generateOpeningLinkForHighlight(
    highlightOpeningId: number,
    flagTool: Flag,
    materialFlagIndexByOpeningId: ReturnType<typeof selectMaterialFlagIndexByOpeningId>,
    drawableLocations: IMUP2DDrawableLocation[]
) {
    const highlightLocation = drawableLocations.find((location) => location.drawable_id === highlightOpeningId)

    if (!highlightLocation) return []

    const nextIndex = isEmpty(materialFlagIndexByOpeningId?.[highlightOpeningId])
        ? 0
        : Object.keys(materialFlagIndexByOpeningId[highlightOpeningId]).length

    // A highlight is always an area shape, so center the flag the same way an area material would.
    const highlightCenterCoordinates: Vector2D = yield call(
        flagTool.getFlagPointFromCoordinates,
        highlightLocation.coordinates,
        true,
        nextIndex
    )

    const openingLink: NewOpeningLink = {
        opening_id: highlightOpeningId,
        document_chunk_id: highlightLocation.document_chunk_id,
        coordinates: highlightCenterCoordinates,
    }

    return [openingLink]
}

export function* handleCreateFlag({ payload }: ReturnType<typeof createFlagAction>) {
    try {
        const manager: PaperManager | null = yield call(managers.get2DManager)

        if (!manager) return

        const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)
        const flagTool: Flag = yield call(manager.getTool, Flag.NAME)

        const project: Project = yield select(selectProject)
        const activeFloorIds: number[] = yield select(selectActiveDocumentChunkIds)
        const activeGeometryGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)
        const activeHighlightOpeningId: number | null = yield select(selectActiveHighlightOpeningId)
        const drawableLocations: IMUP2DDrawableLocation[] = yield select(selectDrawableLocations)
        const materialFlagIndexByOpeningId: ReturnType<typeof selectMaterialFlagIndexByOpeningId> = yield select(
            selectMaterialFlagIndexByOpeningId
        )

        const azureFeatureFlags: AzureFeatureFlag[] | null = yield select(selectAzureFeatureFlags)
        const isQRMCommentsEnabled: boolean = isAzureFeatureFlagEnabled(
            azureFeatureFlags,
            AzureFeatureFlagIds.qrm_comments_attachments_mvp
        )

        const flagFilters: FlagFilters = yield select(selectFlagFilters)

        const temporaryFlagItems: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) => data?.toolName === Flag.NAME && data?.isTemporaryGroup
        )

        const openingLinks: NewOpeningLink[] = []

        // Check if there are active materials
        const activeDrawables = activeGeometryGroup?.openings?.filter((drawable) => drawable.isActive) || []

        // in case when a flag is created from material itself (no temporary flags placed on canvas)
        if (!temporaryFlagItems.length && activeDrawables.length > 0) {
            const links: NewOpeningLink[] = yield* generateOpeningLinksForDrawables(
                activeDrawables,
                workspaceTool,
                flagTool,
                materialFlagIndexByOpeningId
            )

            openingLinks.push(...links)
        } else if (!temporaryFlagItems.length && activeHighlightOpeningId != null) {
            // in case when a flag is created from the "+" button while a highlight is active
            const links: NewOpeningLink[] = yield* generateOpeningLinkForHighlight(
                activeHighlightOpeningId,
                flagTool,
                materialFlagIndexByOpeningId,
                drawableLocations
            )

            openingLinks.push(...links)
        } else if (temporaryFlagItems.length > 0) {
            // Temporary flags placed on canvas
            temporaryFlagItems.forEach((flag) => {
                openingLinks.push({
                    opening_id: flag.data.opening_id,
                    coordinates: [flag.bounds.bottomCenter.x, flag.bounds.bottomCenter.y],
                    document_chunk_id: flag.data.document_chunk_id,
                })
            })

            // Additionally, include all active materials even if flag was placed on canvas
            if (activeDrawables.length > 0) {
                const unlinkedDrawables = activeDrawables.filter(
                    (drawable) => !openingLinks.some((link) => link.opening_id === drawable.id)
                )

                if (unlinkedDrawables.length > 0) {
                    const links: NewOpeningLink[] = yield* generateOpeningLinksForDrawables(
                        unlinkedDrawables,
                        workspaceTool,
                        flagTool,
                        materialFlagIndexByOpeningId
                    )

                    openingLinks.push(...links)
                }
            }

            // Additionally, include the active highlight even if the flag was placed on canvas
            // somewhere other than on top of it
            if (
                activeHighlightOpeningId != null &&
                !openingLinks.some((link) => link.opening_id === activeHighlightOpeningId)
            ) {
                const links: NewOpeningLink[] = yield* generateOpeningLinkForHighlight(
                    activeHighlightOpeningId,
                    flagTool,
                    materialFlagIndexByOpeningId,
                    drawableLocations
                )

                openingLinks.push(...links)
            }
        }

        const response = yield call(createFlag, project.id, {
            ...filterNullValuesFromObject(payload),
            error_id: isQRMCommentsEnabled ? payload.error_id : null,
            opening_links: openingLinks,
        } as FlagAPIPayload)

        const updatedOpeningLinks: OpeningLink[] = response?.opening_links.filter((link: OpeningLink) =>
            activeFloorIds.includes(link.document_chunk_id)
        )

        if (!!updatedOpeningLinks?.length) {
            // remove flags
            yield all(temporaryFlagItems.map((item) => item.remove()))

            const type = getFlagTypeById(response.type_id)

            // add saved flags to blueprint
            const flags = yield all(
                updatedOpeningLinks.map((link) =>
                    call(
                        flagTool.drawFlagByCoords,
                        link.coordinates,
                        response.id,
                        link.opening_id,
                        response.order,
                        type,
                        isFlagVisibleBasedOnFilters(
                            flagFilters,
                            type,
                            response.status_id,
                            response?.creationJiraStatus?.id
                        ),
                        response.status_id,
                        response?.creationJiraStatus?.id || null,
                        false,
                        link.id
                    )
                )
            )

            yield all(flags.map((flag) => call(addTooltipsToFlag, flag, workspaceTool)))

            // save flags in store
            yield put(
                addNewFlag({
                    flag: {
                        ...response,
                        reply_count: 0,
                    },
                })
            )
        }
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForCreateFlag(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(createFlagAction.type, handleCreateFlag)
}
